import * as React from 'react';

import { useAccountLink } from '../plaid/useAccountLink';

interface IAlertReconnectFlowProps {
  bankConnectionId: string;
  /** The user linked, or backed out without linking. */
  onDone: () => void;
  /** The link failed. Never arrives as `onDone` — the two outcomes are disjoint. */
  onError: () => void;
}

/**
 * Reconnects one bank connection through Plaid as soon as it mounts, skipping
 * the institution-selection modal. Renders nothing — mounting is the trigger,
 * and every outcome leaves through a callback.
 *
 * A successful reconnect dismisses the connection's alert server-side, so the
 * row it was started from leaves the list without the caller doing anything.
 */
const AlertReconnectFlow = ({ bankConnectionId, onDone, onError }: IAlertReconnectFlowProps) => {
  const { trigger, errorCode } = useAccountLink({
    bankConnectionId,
    onLinked: onDone,
    onCancelled: onDone,
  });

  React.useEffect(() => {
    trigger();
  }, [trigger]);

  // An error resolves neither callback inside the hook, and the skip-modal path
  // deliberately has nowhere to show one — so report it up, where the caller can
  // both render it and release the row for another attempt.
  React.useEffect(() => {
    if (errorCode) {
      onError();
    }
  }, [errorCode, onError]);

  return null;
};

export default AlertReconnectFlow;
